import { useTranslation } from "react-i18next"

import { LoadingState } from "@/components/loading-state"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useContracts } from "@/features/contracts/hooks/use-contracts"
import { Info } from "lucide-react"
import type { Contract } from "@/types/domain"

interface StepContractProps {
  contractId: string | null
  onContractChange: (id: string) => void
  otNumber: string
  onOtNumberChange: (value: string) => void
}

export function StepContract({
  contractId,
  onContractChange,
  otNumber,
  onOtNumberChange,
}: StepContractProps) {
  const { t } = useTranslation(["transit", "intake", "contracts"])
  const { data: contracts, isLoading } = useContracts()

  const list = contracts ?? []
  const selected = list.find((c) => String(c.id) === contractId) ?? null

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">{t("wizard.step1.intro")}</p>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <Label>{t("wizard.step1.contractLabel")}</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={t("wizard.step1.contractHelp")}
                className="text-muted-foreground hover:text-foreground"
              >
                <Info className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t("wizard.step1.contractHelp")}</TooltipContent>
          </Tooltip>
        </div>
        {isLoading ? (
          <LoadingState />
        ) : (
          <Select value={contractId ?? ""} onValueChange={onContractChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("wizard.step1.contractPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {list.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  <span className="font-medium">{c.clientName ?? "—"}</span>
                  <span className="text-muted-foreground">
                    {" — "}
                    {c.contractNumber ?? c.id}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <Label htmlFor="ot-number-input">
            {t("wizard.step1.otNumberLabel")}{" "}
            <span className="text-muted-foreground font-normal">
              {t("wizard.step1.otNumberOptional")}
            </span>
          </Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={t("wizard.step1.otNumberHelp")}
                className="text-muted-foreground hover:text-foreground"
              >
                <Info className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t("wizard.step1.otNumberHelp")}
            </TooltipContent>
          </Tooltip>
        </div>
        <Input
          id="ot-number-input"
          value={otNumber}
          onChange={(e) => onOtNumberChange(e.target.value)}
          placeholder={t("wizard.step1.otNumberPlaceholder")}
        />
        <p className="text-muted-foreground text-[11px]">
          {t("wizard.step1.otNumberHint")}
        </p>
      </div>

      {selected ? <ContractSummary contract={selected} /> : null}
    </div>
  )
}

function ContractSummary({ contract }: { contract: Contract }) {
  const { t, i18n } = useTranslation(["transit", "intake", "contracts"])

  const product = contract.cocoaGrade
    ? t(`grade.${contract.cocoaGrade}`, {
        ns: "intake",
        defaultValue: contract.cocoaGrade,
      })
    : contract.productType

  const certifications = contract.certificates?.length
    ? contract.certificates
        .map((c) =>
          t(`options.certificates.${c}`, { ns: "contracts", defaultValue: c }),
        )
        .join(", ")
    : t("wizard.step1.none")

  return (
    <Card className="bg-muted/40 flex flex-col gap-3 p-4">
      <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wide">
        {t("wizard.step1.autofilled")}
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Row label={t("wizard.step1.client")} value={contract.clientName} />
        <Row
          label={t("wizard.step1.incoterm")}
          value={contract.incoterms}
        />
        <Row
          label={t("wizard.step1.destinationPort")}
          value={contract.destinationPort}
        />
        <Row label={t("wizard.step1.product")} value={product} />
        <Row
          label={t("wizard.step1.price")}
          value={
            contract.basePrice
              ? `${contract.basePrice} ${contract.currency ?? ""} / MT`
              : null
          }
        />
        <Row
          label={t("wizard.step1.paymentTerms")}
          value={contract.paymentTerms}
        />
        <Row
          label={t("wizard.step1.contractDate")}
          value={formatDate(contract.contractDate, i18n.language)}
        />
        <Row
          label={t("wizard.step1.deliveryWindow")}
          value={formatRange(
            contract.contractDurationStart,
            contract.contractDurationEnd,
            i18n.language,
          )}
        />
        <Row
          label={t("wizard.step1.certifications")}
          value={certifications}
        />
      </div>
    </Card>
  )
}

function formatDate(value: string | null | undefined, locale: string): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  try {
    return d.toLocaleDateString(locale, { dateStyle: "medium" })
  } catch {
    return d.toISOString().slice(0, 10)
  }
}

function formatRange(
  start: string | null | undefined,
  end: string | null | undefined,
  locale: string,
): string | null {
  const a = formatDate(start, locale)
  const b = formatDate(end, locale)
  if (!a && !b) return null
  if (a && b) return `${a} → ${b}`
  return a ?? b
}

function Row({
  label,
  value,
}: {
  label: string
  value: string | null | undefined
}) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
        {label}
      </div>
      <div className="text-foreground mt-0.5 truncate text-sm">
        {value || "—"}
      </div>
    </div>
  )
}
